"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Building2, Calendar, FileText, ChevronRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const projectHighlights = [{
  title: "Phased access handover",
  desc: "Sequential elevation strikes and re-erections scheduled around ongoing commercial tenant operations."
}, {
  title: "Weekly TG20:21 inspections",
  desc: "Recorded statutory inspections, handover certificates, and direct site supervisor communication."
}, {
  title: "Zero unplanned downtime",
  desc: "Accurate material loading calculations avoiding delays for external envelope and glazing trades."
}];
const accreditationBadges = [{
  name: "NASC",
  label: "Audited member"
}, {
  name: "CHAS",
  label: "Accredited contractor"
}];
const sitePhotoQuery = new WP_Query({
  post_type: "site_photo",
  per_page: 50
});
function FacilityPhotoImage() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="h-full w-full bg-muted animate-pulse" />;
  }
  const matchedPhoto = posts.find(p => p.title === "Property Facilities Management Image" || p.customFields?.photo_name === "Property Facilities Management Image");
  const photoSrc = matchedPhoto?.customFields?.image || matchedPhoto?.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/refurbishment_scaffolding_project.png";
  const photoAlt = matchedPhoto?.customFields?.alt_text || "Multi-storey commercial refurbishment scaffolding structure";
  const isDynamic = !!(matchedPhoto?.customFields?.image || matchedPhoto?.featuredImage);
  return <img src={photoSrc} data-wvc-srcset={isDynamic ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/refurbishment_scaffolding_project.png-wvc-srcset"} data-wvc-sizes={isDynamic ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/refurbishment_scaffolding_project.png-wvc-sizes"} alt={photoAlt} loading="lazy" decoding="async" className="h-full w-full object-cover" />;
}
export default function Partnership() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="longtermrelationshipcase" data-nav="light" className="relative bg-background py-16 sm:py-24">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8 items-start">
          {/* Left Column: Context, Case Narrative & CTAs */}
          <motion.div variants={itemVariants} className="lg:col-span-7 flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="border-border text-foreground font-medium">
                Case study
              </Badge>
              <Badge variant="secondary" className="bg-muted text-muted-foreground font-normal">
                Multi-phase commercial refurbishment
              </Badge>
            </div>

            <div className="space-y-3">
              <h2 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                Building dependable contractor partnerships
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Consistent safety standards, transparent scheduling, and proactive site support across multi-phase programmes.
              </p>
            </div>

            <div className="space-y-4 text-foreground/90 text-base leading-relaxed">
              <p>
                When working across complex refurbishment contracts, main contractors and building envelope specialists require access partners who respect strict programme deadlines and public safety boundaries.
              </p>
              <p>
                Our 20+ years of operational leadership ensure full design compliance from initial technical drawings to dismantle. By maintaining direct director-level contact and full site accountability, we help partners reduce risk and eliminate turnaround bottlenecks.
              </p>
            </div>

            {/* Key Deliverables Grid */}
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
              {projectHighlights.map((highlight, index) => <div key={highlight.title} data-index={index} className="rounded-lg border border-border bg-card p-4 shadow-xs flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-primary">
                    <CheckCircle2 className="size-4 shrink-0" />
                    <span className="font-semibold text-sm text-card-foreground">
                      {highlight.title}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {highlight.desc}
                  </p>
                </div>)}
            </div>

            <Separator className="my-2" />

            {/* Navigation & Action Links */}
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactRequestQuotePage">
                  Discuss a project
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/ContactRequestQuotePage">
                  Request a quote
                </Link>
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-2 text-sm text-muted-foreground">
              <Link to="/RefurbishmentScaffoldingPage" className="inline-flex items-center gap-1 font-medium text-foreground hover:text-primary transition-colors">
                Refurbishment scaffolding details
                <ChevronRight className="size-4" />
              </Link>
              <Link to="/ProjectsCaseStudiesPage" className="inline-flex items-center gap-1 font-medium text-foreground hover:text-primary transition-colors">
                View all case studies
                <ChevronRight className="size-4" />
              </Link>
            </div>
          </motion.div>

          {/* Right Column: Case Summary Card & Accreditations */}
          <motion.div variants={itemVariants} className="lg:col-span-5 flex flex-col gap-6">
            <Card className="border-border bg-card shadow-md overflow-hidden">
              <div className="relative aspect-video w-full overflow-hidden bg-muted">
                <PostsProvider wp_query={{
                post_type: "site_photo",
                per_page: 50,
                status: ["publish"]
              }} className="h-full w-full">
                  <FacilityPhotoImage />
                </PostsProvider>
                <div className="absolute top-3 left-3 pointer-events-none">
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-secondary/90 backdrop-blur-xs px-2.5 py-1 text-xs font-medium text-secondary-foreground">
                    <Building2 className="size-3.5" />
                    Commercial Framework
                  </span>
                </div>
              </div>

              <CardHeader className="gap-2 pb-3">
                <CardTitle className="text-xl font-semibold text-card-foreground">
                  South East Commercial Refurbishment
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm">
                  Full external envelope and high-level structural roof access for phased contractor works.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-4 rounded-lg bg-muted/40 p-3.5 border border-border/60">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate> Project Duration
                    </span>
                    <span className="font-medium text-card-foreground">14-week phased access</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <FileText className="size-3.5" /> Access Scope
                    </span>
                    <span className="font-medium text-card-foreground">Independent & roof bridge</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs font-semibold text-foreground tracking-wide uppercase">
                    <ShieldCheck className="size-4 text-primary" /> Verified site compliance
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Designed strictly to TG20:21 compliant standards with custom loading bays, pedestrian gantries, and debris netting to maintain ground-level safety.
                  </p>
                </div>
              </CardContent>

              <CardFooter className="flex flex-col gap-3 pt-2 border-t border-border bg-muted/20">
                <div className="flex items-center justify-between w-full">
                  <span className="text-xs font-medium text-muted-foreground">Certified standards:</span>
                  <div className="flex items-center gap-2">
                    {accreditationBadges.map((badge, bIndex) => <Badge key={badge.name} data-index={bIndex} variant="outline" className="bg-card text-foreground text-[11px] px-2 py-0.5">
                        {badge.name}
                      </Badge>)}
                  </div>
                </div>
              </CardFooter>
            </Card>

            {/* Operational Commitment Box */}
            <div className="rounded-lg border border-border bg-card p-5 shadow-xs">
              <h3 className="text-sm font-semibold text-card-foreground mb-1.5">
                Director-led accountability
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Every long-term contract is overseen directly by Pete Wood and experienced senior supervisors, ensuring quick resolutions and clear handover reports.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}